import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CaretLeft, CaretRight, WarningCircle, Warning, Gavel, CaretRight as Chevron, UsersThree, Scales,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { todayKey } from '@/lib/diaryStore';
import {
  fetchOperationsDiary, fetchDiaryIntelligence, type DiaryItem, type DiaryWarning,
} from '@/lib/operationsApi';

/**
 * Clerk Control Tower — the clerk's operational surface across supported barristers.
 * PRODUCTION feature. Consumes ONLY the shared Stage-3 platform (`/operations/diary` +
 * `/operations/intelligence`, scope=supported) — no bespoke API, no duplicated business logic.
 * It controls scope · grouping · aggregation · presentation · navigation, nothing more.
 */
export default function ControlTowerPage() {
  const navigate = useNavigate();
  const [day, setDay] = useState<string>(() => todayKey());

  const from = `${day}T00:00:00.000Z`;
  const to = `${day}T23:59:59.999Z`;

  const diaryQ = useQuery({
    queryKey: ['ct-diary', day],
    queryFn: () => fetchOperationsDiary({ scope: 'supported', from, to }),
    staleTime: 60_000,
  });
  const intelQ = useQuery({
    queryKey: ['ct-intel', day],
    queryFn: () => fetchDiaryIntelligence({ scope: 'supported', from, to }),
    staleTime: 60_000,
  });

  const items = diaryQ.data ?? [];
  const warnings = intelQ.data ?? [];

  // Both panels below are "render only when there is something to say": the Needs-you card only
  // appears when a warning exists, and the empty card only when the diary is empty. A failed
  // fetch produces both conditions at once, so the board read as a calm, covered day — on the
  // screen a clerk uses to decide whether counsel is needed anywhere. Say it is incomplete.
  const diaryFailed = diaryQ.isError;
  const intelFailed = intelQ.isError;

  // Group by barrister (owner). Unassigned events surface as a coverage gap.
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; items: DiaryItem[] }>();
    for (const it of items) {
      const key = it.owner?.id ?? '__unassigned__';
      const name = it.owner?.name ?? 'Unassigned';
      const g = map.get(key) ?? { name, items: [] };
      g.items.push(it);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => {
      if (a.name === 'Unassigned') return -1;
      if (b.name === 'Unassigned') return 1;
      return b.items.length - a.items.length; // busiest first
    });
  }, [items]);

  const actWarnings = warnings.filter((w) => w.severity === 'act');
  const watchWarnings = warnings.filter((w) => w.severity === 'watch');
  const uncovered = items.filter((i) => !i.owner).length;

  const shiftDay = (delta: number) => {
    const d = new Date(`${day}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + delta);
    setDay(d.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-5 px-1 py-2">
      {/* Header + day nav */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UsersThree className="h-6 w-6 text-brand-600 dark:text-brand-400" weight="duotone" />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground">Control Tower</h1>
            <p className="text-xs text-muted-foreground">Coverage &amp; readiness across your barristers</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftDay(-1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted"><CaretLeft className="h-4 w-4" /></button>
          <button onClick={() => setDay(todayKey())} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">Today</button>
          <button onClick={() => shiftDay(1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted"><CaretRight className="h-4 w-4" /></button>
          <span className="ml-2 text-sm font-medium text-foreground">
            {new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
        </div>
      </div>

      {(diaryFailed || intelFailed) && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>
            Couldn't load {[diaryFailed && 'the day\u2019s listings', intelFailed && 'the readiness warnings'].filter(Boolean).join(' or ')} —
            this board is incomplete. Do not read it as a covered day.
          </span>
          <Button variant="outline" size="sm" onClick={() => { if (diaryFailed) void diaryQ.refetch(); if (intelFailed) void intelQ.refetch(); }}>
            Try again
          </Button>
        </div>
      )}

      {/* NEEDS YOU — coverage gaps + warnings (act first) */}
      {(actWarnings.length > 0 || uncovered > 0 || watchWarnings.length > 0) && (
        <Card className="border-l-4 border-l-amber-400">
          <CardContent className="space-y-2 p-4">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
              <WarningCircle className="h-4 w-4" weight="fill" /> Needs you
            </div>
            {uncovered > 0 && (
              <Row tone="act" icon={<Gavel className="h-4 w-4" />} text={`${uncovered} uncovered hearing${uncovered === 1 ? '' : 's'} — no counsel assigned`} />
            )}
            {actWarnings.map((w, i) => (
              <Row key={`a${i}`} tone="act" icon={warnIcon(w)} text={w.detail} onClick={() => goToWarning(navigate, w)} />
            ))}
            {watchWarnings.slice(0, 6).map((w, i) => (
              <Row key={`w${i}`} tone="watch" icon={warnIcon(w)} text={w.detail} onClick={() => goToWarning(navigate, w)} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Loading / empty */}
      {(diaryQ.isLoading || intelQ.isLoading) && <div className="text-sm text-muted-foreground">Loading operations…</div>}
      {!diaryQ.isLoading && !diaryFailed && items.length === 0 && (
        <Card><CardContent className="flex flex-col items-center gap-2 py-14 text-center">
          <Scales className="h-9 w-9 text-muted-foreground/30" weight="duotone" />
          <div className="text-sm font-semibold text-foreground">Nothing listed for your barristers</div>
          <p className="text-xs text-muted-foreground">No hearings or events on {new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}.</p>
        </CardContent></Card>
      )}

      {/* Per-barrister coverage */}
      {groups.map((g) => (
        <Card key={g.name}>
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${g.name === 'Unassigned' ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{g.name}</span>
              <Badge variant="outline">{g.items.length}</Badge>
            </div>
          </div>
          <CardContent className="p-0">
            {g.items
              .sort((a, b) => a.startAt.localeCompare(b.startAt))
              .map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => (it.hearing ? navigate(`/clerk/hearings/${it.hearing.id}`) : it.matter ? navigate(`/clerk/matters/${it.matter.id}`) : undefined)}
                  className="flex w-full items-center gap-3 border-b border-border/40 px-4 py-2.5 text-left transition last:border-0 hover:bg-muted/30"
                >
                  <span className="w-12 shrink-0 text-sm font-semibold tabular-nums text-foreground">{it.startAt.slice(11, 16)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-foreground">
                      {it.matter ? `${it.matter.number} · ${it.matter.name}` : it.title}
                    </div>
                    <div className="truncate text-[11px] capitalize text-muted-foreground">
                      {[it.hearing?.type, it.matter?.court, it.matter?.client].filter(Boolean).join(' · ') || it.type}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {it.readiness && it.readiness.percentage < 100 && (
                      <Badge variant={it.readiness.percentage < 50 ? 'danger' : 'warning'}>{it.readiness.percentage}%</Badge>
                    )}
                    {it.obligations && it.obligations.blocked > 0 && (
                      <Badge variant="danger">{it.obligations.blocked} blocked</Badge>
                    )}
                    {it.money?.outstanding && <Badge variant="warning">£ due</Badge>}
                    {(it.matter?.priority === 'high' || it.matter?.priority === 'urgent') && (
                      <Badge variant={it.matter.priority === 'urgent' ? 'danger' : 'warning'} className="capitalize">{it.matter.priority}</Badge>
                    )}
                    <Chevron className="h-4 w-4 text-muted-foreground/40" />
                  </div>
                </button>
              ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Row({ tone, icon, text, onClick }: { tone: 'act' | 'watch'; icon: React.ReactNode; text: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${onClick ? 'cursor-pointer hover:bg-muted/40' : ''} ${
        tone === 'act' ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'
      }`}
    >
      <span className="shrink-0">{icon}</span>
      <span className="flex-1">{text}</span>
    </div>
  );
}

function warnIcon(w: DiaryWarning): React.ReactNode {
  if (w.kind === 'double_book') return <WarningCircle className="h-4 w-4" weight="fill" />;
  if (w.kind === 'missing_prep') return <Gavel className="h-4 w-4" />;
  return <Warning className="h-4 w-4" weight="fill" />;
}

function goToWarning(navigate: ReturnType<typeof useNavigate>, w: DiaryWarning) {
  if (w.subjectType === 'hearing') navigate(`/clerk/hearings/${w.subjectId}`);
  else if (w.subjectType === 'matter') navigate(`/clerk/matters/${w.subjectId}`);
}
